<template lang="pug">
	.geo-fencing-map.flex-col.space
		template(v-if="data.geoFencingPoints.length")
			.markers.flex-row.space.align-center
				.geo-place.flex-row.space.align-center(v-for="(point, i) in data.geoFencingPoints")
					span(@click="setPlace(point.place, point.radius / 1000)") {{point.place.name}}
					.adt-button.clickable.align-center.center.flex-row.rounded(v-if="!readonly", icon="close", stylingMode="text", type="default", @click="removeGeoFencing(i)")
						fas(icon="times", size="xs")
				Loader(v-if="imageProcessing", text="")
		.map-holder
			.region-controls(v-show="!readonly")
				DxTextBox.region-input(ref="regionInput", :placeholder="$tCaption('Start typing to find regions..')")
				DxNumberBox.region-radius(ref="radiusInput", :format="radiusFormat", :value="radius", @value-changed="v => radius = v.value")
			.map
		.screener
			MapScreener(v-if="makingScreenshot", :geoJsons="screenerItems", @image-generated="onImageGenerated", :width="1920 / 2", :height="1280 / 2", @start-generation="imageProcessing = true")
</template>

<script>
	import $ from 'jquery';
	import _ from 'lodash';
	import VueTypes from 'vue-types';
	import dynamic from 'dynamic';
	import mapsHelper from './maps-helper.js';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxButton from 'devextreme-vue/button';
	import DxNumberBox from 'devextreme-vue/number-box';
	import MapScreener from './MapScreener.vue';
	import Loader from 'app-core/Loader.vue';
	import circleToPolygon from 'circle-to-polygon';

	export default {
		translationCategory: 'maps.GeoFencingMap',
		name: 'radio-map',
		components: {
			DxTextBox,
			DxNumberBox,
			MapScreener,
			DxButton,
			Loader
		},
		props: {
			data: VueTypes.any.def(null),
			mapPosition: VueTypes.object.def({}),
			readonly: VueTypes.bool.def(false)
		},
		data() {
			return {
				radius: 10,
				drawnItems: null,
				autocomplete: null,
				popup: {
					loading: false,
					visible: false,
					data: null
				},
				screenerItems: [],
				imageProcessing: false,
				makingScreenshot: 0
			};
		},
		computed: {
			radiusFormat() {
				return `${this.$tCaption('Radius')}: #0 ${mapsHelper.kmOrMiles()}`;
			}
		},
		watch: {
			'data.geoFencingPoints': {
				handler(v) {
					if (this.$Data.safeStringify(v) !== this.$lastBuiltPlaces) {
						this.buildPlaces();
						this.$lastBuiltPlaces = this.$Data.safeStringify(v);
					}
				},
				deep: true
			},
			radius() {
				this.addPlace();
			}
		},
		mounted() {
			window.jQuery = $;
			dynamic.loadOnce([
				'https://unpkg.com/leaflet@1.5.1/dist/leaflet.js',
				'https://unpkg.com/leaflet@1.5.1/dist/leaflet.css',
			]).then(() => {
				dynamic.loadOnce([
					'https://maps.googleapis.com/maps/api/js?key=AIzaSyBxX6xc0TryC8oGKqVLaIIyJT9w46OYsLE&libraries=places,geometry&language=en&js',
					'https://cdnjs.cloudflare.com/ajax/libs/leaflet.draw/0.4.13/leaflet.draw.js',
					'https://cdnjs.cloudflare.com/ajax/libs/leaflet.draw/0.4.13/leaflet.draw.css',
					'https://cdn.jsdelivr.net/npm/leaflet-geometryutil@0.9.3/src/leaflet.geometryutil.min.js'
				]).then(() => {
					this.buildMap();
					this.buildPlaces();
				});
			});

			if (__DEBUG__)
				window.geoFencingMap = this;
		},
		destroyed() {
			delete this.$map;
		},
		methods: {
			onImageGenerated(dataURI) {
				this.$emit('image-generated', dataURI);
				this.$set(this.data, 'imageGeofencing', dataURI);
				if (this.makingScreenshot === 1) {
					this.makingScreenshot = 0;
					this.imageProcessing = false;
				} else {
					this.makingScreenshot = 0;
					this.$nextTick(() => this.makingScreenshot++);
				}
			},
			setPlace(place, radius) {
				this.autocompletePlace = place;
				if (radius) {
					this.radius = radius;
				}
				this.$refs.regionInput.instance.option('value', place.name);
			},
			removeGeoFencing(index) {
				this.$confirmMessage({
					title: () => this.$tCaption('Confirm action', '@actions'),
					text: this.$tCaption('Are you sure you want to remove area?'),
					handler: () => {
						const points = this.data.geoFencingPoints;
						points.splice(index, 1);
						if (points?.length) {
							this.setPlace(points[Math.max(0, index-1)].place, points[Math.max(0, index-1)].radius/1000);
						}
						this.generateScreen();
					}
				});
			},
			generateScreen() {
				this.screenerItems = this.data.geoFencingPoints.map((f, i) => 
					circleToPolygon([f.lng, f.lat], parseInt(f.radius), { numberOfEdges: 32 })
				);
				this.$nextTick(() => this.makingScreenshot++);
			},
			buildPlaces() {
				if (__DEBUG__)
					console.debug('Build geo fencing places');
				this.drawnItems.clearLayers();
				this.data.geoFencingPoints.map((f, i) => {
					window.L.circle([f.lat, f.lng], {
						index: i,
						radius: f.radius,
						fillColor: '#999',
						color: '#333',
						fill: true,
						opacity: 0.7
					}).addTo(this.drawnItems);
				});
				this.$map.fitBounds(this.drawnItems.getBounds());
			},
			addPlace() {
				const place = this.autocompletePlace;
				if (place?.geometry) {
					const lat = parseFloat(place.lat);
					const lng = parseFloat(place.lng);
					const radius = this.radius * mapsHelper.metricMultiplier();
					if (__DEBUG__)
						console.log('Place changed', { place, lat, lng, radius });
					const location = {
						place,
						radius,
						lat,
						lng
					};
					var existingIndex = _.findIndex(this.data.geoFencingPoints, f => f.place.place_id === place.place_id);
					if (existingIndex > -1) {
						_.assign(this.data.geoFencingPoints[existingIndex], location);
					} else {
						this.data.geoFencingPoints.push(location);
					}
				}
			},
			buildMap() {
				if (__DEBUG__)
					console.debug('Build geofencing map');

				const $el = this.$el;
				const L = window.L;
				const map = this.$map = L.map($(this.$el).find('.map')[0]).setView([this.mapPosition.lat || 37.8, this.mapPosition.lng || -96], this.mapPosition.zoom || 5);

				L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}@2x.png', {
					maxZoom: 18
				}).addTo(map);


				this.autocomplete = new google.maps.places.Autocomplete(
					($($el).find('.region-input input')[0])
				);
				this.autocomplete.addListener('place_changed', () => {
					const place = this.autocomplete.getPlace();
					place.lat = parseFloat(place.geometry.location.lat());
					place.lng = parseFloat(place.geometry.location.lng());
					this.setPlace(place);
					this.addPlace();
					this.generateScreen();
				});
				$($el).find('.region-input input').attr('placeholder', ' ');

				this.drawnItems = L.featureGroup().addTo(map);

				if (!this.mapPosition?.lat && !this.data?.geoFencingPoints?.length) {
					this.fitUserLocation();
				}
			},
			async fitUserLocation() {
				let userLocationInfo = await this.$auth.getUserLocation();

				const bounds = userLocationInfo.bounds;
				if (bounds) {
					this.$map.fitBounds(
						[
							[bounds[0], bounds[2]],
							[bounds[1], bounds[3]]
						]
					);
				}
			}
		}
	};
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.geo-fencing-map {
		.markers {
			border-radius: 4px;

			> * {
				margin-right: 4px;
			}
		}

		.geo-place {
			padding: 6px 12px;
			background: rgba(0,0,0,0.05);
			font-size: 16px;
			font-weight: 100;
			color: $base-accent;
			border-radius: 4px;
			cursor: pointer;
			transition: 0.3s;

			&:hover {
				background: rgba(0,0,0,0.1);
			}
		}

		.map-holder {
			height: calc(100vh - 300px);
			width: 100%;
			max-height: calc(100vh - 300px);
			min-height: 240px;
			margin-top: 0px;
			position: relative;

			.map {
				width: 100%;
				height: 100%;
				border-radius: 4px;
			}

			> .proposal-buttons-group {
				position: absolute;
				left: 10px;
				bottom: 10px;
			}

			> .region-controls {
				padding: 15px 15px 15px 14px;
				background: rgba(255, 255, 255, 0.95);
				border-radius: 4px;
				position: absolute;
				top: 6px;
				left: 6px;
				width: 500px;
				max-width: 80%;
				z-index: 1000;
				box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
				display: flex;
				flex-direction: row;
				margin-left: 50px;

				> .region-input {
					flex: 5;
					margin-right: 10px;

					&.dx-texteditor.dx-editor-underlined:after {
						border-bottom: 1px solid rgba(70, 70, 70, 0.13);
					}
				}

				.region-radius {
					flex: 2;
				}
			}
		}
	}
</style>
